import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Loader2,
  Gauge,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: "jfs_01234",
    q: "Are all currently-enrolled children's DCY 01234 forms signed and updated within the last 12 months?",
    options: [
      { id: "yes", label: "Yes, all current" },
      { id: "partial", label: "Mostly, some are overdue" },
      { id: "no", label: "No / we're not sure" },
    ],
  },
  {
    id: "ratios",
    q: "How often do you verify OAC 5101:2-12-18 staff-to-child ratios?",
    options: [
      { id: "realtime", label: "Live / every room, every moment" },
      { id: "daily", label: "Daily head-count" },
      { id: "weekly", label: "A few times a week" },
      { id: "inspection", label: "Mostly at inspection time" },
    ],
  },
  {
    id: "incident_reports",
    q: "How are incident / injury reports (DCY 01299) filed?",
    options: [
      { id: "digital", label: "Digital — timestamped & logged" },
      { id: "paper", label: "Paper forms filed in a binder" },
      { id: "mixed", label: "A mix of both" },
    ],
  },
  {
    id: "medication",
    q: "Is DCY 01217 (Request for Administration of Medication) on file for every child receiving meds?",
    options: [
      { id: "yes", label: "Yes — always" },
      { id: "sometimes", label: "Usually, but sometimes late" },
      { id: "no", label: "No / rarely" },
    ],
  },
  {
    id: "staff_training",
    q: "How do you track staff health training (DCY 01276)?",
    options: [
      { id: "tracked", label: "Tracked per employee with renewal alerts" },
      { id: "manual", label: "Manually in a spreadsheet" },
      { id: "none", label: "Not formally tracked" },
    ],
  },
  {
    id: "fire_drills",
    q: "How often do you log fire drills (CCU-202)?",
    options: [
      { id: "monthly", label: "Every month, logged" },
      { id: "quarterly", label: "Once a quarter" },
      { id: "rarely", label: "Rarely / not logged" },
    ],
  },
  {
    id: "audit_ready",
    q: "If a licensing specialist walked in today, how long would it take to produce every required doc?",
    options: [
      { id: "yes", label: "Under 10 minutes — we're audit-ready" },
      { id: "hour", label: "About an hour" },
      { id: "day", label: "A full day or more" },
    ],
  },
];

export const ComplianceQuiz = () => {
  const [step, setStep] = useState(0); // 0 = intro, 1..N = questions, N+1 = email, N+2 = result
  const emailStep = QUESTIONS.length + 1;
  const resultStep = QUESTIONS.length + 2;

  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [centerName, setCenterName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const currentQuestion = step >= 1 && step <= QUESTIONS.length ? QUESTIONS[step - 1] : null;
  const progress = Math.min(100, ((step - 1) / (QUESTIONS.length + 1)) * 100);

  const selectAnswer = (qid, aid) => setAnswers((a) => ({ ...a, [qid]: aid }));

  const next = () => setStep((s) => s + 1);
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!email) {
      toast.error("Please enter your email to see your score.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/compliance-quiz`, {
        email,
        center_name: centerName || null,
        answers,
      });
      setResult(res.data);
      setStep(resultStep);
    } catch (err) {
      console.error(err);
      toast.error("Could not calculate your score. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setEmail("");
    setCenterName("");
    setResult(null);
  };

  return (
    <section
      id="compliance-quiz"
      data-testid="compliance-quiz-section"
      className="py-24 sm:py-32 bg-stone-50 relative overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent" />
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <div className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-emerald-700">
              2-minute check
            </div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium text-stone-900 tracking-tight">
              Calculate your Ohio compliance risk.
            </h2>
            <p className="mt-4 text-stone-600 text-sm leading-relaxed">
              Seven quick questions against Ohio DCY licensing requirements. Get a personalized risk
              score and your top three gaps — emailed to you instantly.
            </p>
            <div className="mt-6 flex items-center gap-2 text-xs text-stone-500">
              <ShieldCheck className="w-4 h-4 text-emerald-700" />
              No spam. We use your email only to send your report.
            </div>
          </div>

          <div className="lg:col-span-8">
            <div
              data-testid="compliance-quiz-card"
              className="rounded-3xl border border-stone-200 bg-white shadow-lift overflow-hidden"
            >
              {/* Progress */}
              {step >= 1 && step <= emailStep && (
                <div className="h-1 w-full bg-stone-100">
                  <div
                    data-testid="quiz-progress"
                    className="h-full bg-emerald-600 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}

              <div className="p-8 sm:p-10 min-h-[380px] flex flex-col">
                {step === 0 && (
                  <IntroStep onStart={next} />
                )}

                {currentQuestion && (
                  <QuestionStep
                    index={step}
                    total={QUESTIONS.length}
                    question={currentQuestion}
                    selected={answers[currentQuestion.id]}
                    onSelect={(aid) => selectAnswer(currentQuestion.id, aid)}
                    onPrev={prev}
                    onNext={next}
                  />
                )}

                {step === emailStep && (
                  <EmailStep
                    email={email}
                    setEmail={setEmail}
                    centerName={centerName}
                    setCenterName={setCenterName}
                    onPrev={prev}
                    onSubmit={submit}
                    loading={loading}
                  />
                )}

                {step === resultStep && result && <ResultStep result={result} onReset={reset} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const IntroStep = ({ onStart }) => (
  <div className="flex-1 flex flex-col justify-center items-start" data-testid="quiz-intro">
    <div className="w-12 h-12 rounded-2xl bg-emerald-700/10 grid place-items-center">
      <Gauge className="w-6 h-6 text-emerald-700" />
    </div>
    <h3 className="mt-5 font-display text-2xl font-medium text-stone-900">Ready? Takes ~ 2 minutes.</h3>
    <p className="mt-3 text-stone-600 text-sm max-w-md">
      Covers DCY 01234, OAC 5101:2-12-18 ratios, DCY 01299 incident reporting, fire drills, staff training, and
      audit-readiness. Based on actual Ohio licensing citations.
    </p>
    <Button
      data-testid="quiz-start-btn"
      onClick={onStart}
      className="mt-7 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-6 py-5 font-semibold hover:-translate-y-0.5 transition-all"
    >
      Start the check <ArrowRight className="ml-2 w-4 h-4" />
    </Button>
  </div>
);

const QuestionStep = ({ index, total, question, selected, onSelect, onPrev, onNext }) => (
  <div className="flex-1 flex flex-col" data-testid={`quiz-question-${index}`}>
    <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald-700">
      Question {index} of {total}
    </div>
    <h3 className="mt-4 font-display text-xl sm:text-2xl font-medium text-stone-900 leading-snug">
      {question.q}
    </h3>
    <div className="mt-6 space-y-2.5 flex-1">
      {question.options.map((opt) => {
        const isSelected = selected === opt.id;
        return (
          <button
            key={opt.id}
            data-testid={`quiz-option-${question.id}-${opt.id}`}
            onClick={() => onSelect(opt.id)}
            className={`w-full text-left rounded-xl border px-5 py-4 text-sm font-medium transition-all ${
              isSelected
                ? "border-emerald-700 bg-emerald-50 text-emerald-900 shadow-sm"
                : "border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50 text-stone-800"
            }`}
          >
            <span className="flex items-center gap-3">
              <span
                className={`w-5 h-5 rounded-full border-2 shrink-0 ${
                  isSelected ? "border-emerald-700 bg-emerald-700" : "border-stone-300"
                } grid place-items-center`}
              >
                {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
              </span>
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
    <div className="mt-6 flex items-center justify-between">
      <Button
        variant="ghost"
        data-testid="quiz-prev-btn"
        onClick={onPrev}
        className="text-stone-500 hover:text-stone-900"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Button>
      <Button
        data-testid="quiz-next-btn"
        onClick={onNext}
        disabled={!selected}
        className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-6 disabled:opacity-40"
      >
        {index === total ? "See my score" : "Next"} <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  </div>
);

const EmailStep = ({ email, setEmail, centerName, setCenterName, onPrev, onSubmit, loading }) => (
  <div className="flex-1 flex flex-col" data-testid="quiz-email-step">
    <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald-700">
      Almost there
    </div>
    <h3 className="mt-4 font-display text-xl sm:text-2xl font-medium text-stone-900">
      Where should we send your compliance report?
    </h3>
    <p className="mt-3 text-stone-500 text-sm max-w-md">
      We'll calculate your score and email a PDF summary with the top 3 gaps and how to close them.
    </p>
    <div className="mt-6 space-y-4 max-w-md">
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Work email *</Label>
        <Input
          data-testid="quiz-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          placeholder="you@yourcenter.com"
          className="mt-2 h-11"
          required
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Center name (optional)</Label>
        <Input
          data-testid="quiz-center-input"
          value={centerName}
          onChange={(e) => setCenterName(e.target.value)}
          placeholder="Columbus Early Learning"
          className="mt-2 h-11"
        />
      </div>
    </div>
    <div className="mt-8 flex items-center justify-between">
      <Button
        variant="ghost"
        data-testid="quiz-email-prev-btn"
        onClick={onPrev}
        className="text-stone-500 hover:text-stone-900"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Button>
      <Button
        data-testid="quiz-submit-btn"
        onClick={onSubmit}
        disabled={loading || !email}
        className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-6"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scoring…</>
        ) : (
          <>Show my score <ArrowRight className="w-4 h-4 ml-1" /></>
        )}
      </Button>
    </div>
  </div>
);

const riskStyles = {
  low: { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", ring: "#047857", label: "Low risk" },
  moderate: { color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200", ring: "#ea580c", label: "Moderate risk" },
  high: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200", ring: "#dc2626", label: "High risk" },
};

const ResultStep = ({ result, onReset }) => {
  const style = riskStyles[result.risk_level] || riskStyles.moderate;
  const angle = (result.score / 100) * 360;
  const bg = `conic-gradient(${style.ring} ${angle}deg, #e7e5e4 ${angle}deg)`;

  return (
    <div className="flex-1 flex flex-col" data-testid="quiz-result">
      <div className="grid sm:grid-cols-5 gap-8 items-center">
        <div className="sm:col-span-2 flex flex-col items-center sm:items-start">
          <div
            data-testid="quiz-score-ring"
            className="relative w-36 h-36 rounded-full grid place-items-center"
            style={{ background: bg }}
          >
            <div className="absolute inset-2 rounded-full bg-white grid place-items-center">
              <div className="text-center">
                <div className="font-display text-4xl font-medium text-stone-900">{result.score}</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500">out of 100</div>
              </div>
            </div>
          </div>
          <div
            className={`mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${style.border} ${style.bg} ${style.color} text-xs font-semibold uppercase tracking-wider`}
          >
            {result.risk_level === "low" ? (
              <ShieldCheck className="w-3.5 h-3.5" />
            ) : (
              <AlertTriangle className="w-3.5 h-3.5" />
            )}
            {style.label}
          </div>
        </div>

        <div className="sm:col-span-3">
          <h3 className="font-display text-2xl font-medium text-stone-900">Your Ohio compliance report</h3>
          <p className="mt-3 text-stone-600 text-sm leading-relaxed">{result.recommendation}</p>
          <div className="mt-5">
            <Button
              asChild
              data-testid="quiz-result-book-demo"
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-5 hover:-translate-y-0.5 transition-all"
            >
              <a href="#demo">Close my gaps with ChildCheck <ArrowRight className="w-4 h-4 ml-1" /></a>
            </Button>
            <Button
              data-testid="quiz-result-reset"
              variant="ghost"
              onClick={onReset}
              className="ml-2 text-stone-500"
            >
              Retake
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5" data-testid="quiz-strengths">
          <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> Strengths
          </div>
          {result.strengths.length === 0 ? (
            <p className="mt-3 text-sm text-stone-600">No major strengths detected — let's fix that.</p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm text-stone-700">
              {result.strengths.slice(0, 4).map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" /> {s}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-orange-200 bg-orange-50/60 p-5" data-testid="quiz-gaps">
          <div className="text-[11px] uppercase tracking-widest font-bold text-orange-800 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Top gaps to close
          </div>
          {result.gaps.length === 0 ? (
            <p className="mt-3 text-sm text-stone-600">Zero gaps detected. Beautiful work.</p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm text-stone-700">
              {result.gaps.slice(0, 4).map((g, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></span> {g}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceQuiz;
